// Static registry of "Claude AI Developer V4" (CD04) course lessons.
//
// CD04 is the FINAL cut of the course: the lesson list was chosen by hand from
// CD01, CD02 and CD03 rather than written fresh. It is a SEPARATE course again —
// all three earlier ones stay live and byte-for-byte untouched, nobody mid-course
// is disturbed, and no progress is migrated.
//
// 43 lessons across 5 modules, numbered in true display order (id, route and
// position in this array all agree, unlike CD02, where V2611 reads as lesson 5).
//
// WHAT IT TAKES FROM WHERE, and why:
//
//   Module 1 (9) — CD02's Module 1, plus CD01's "what a programming language is".
//     CD03 had dropped both that lesson and "the three languages of the web";
//     this course puts them back, because Module 3 no longer opens on a
//     first-look lesson that introduced them in passing.
//   Module 2 (7) — CD02/CD03's Module 2, unchanged in all three courses.
//   Module 3 (10) — the hybrid. CD03's reading pages come FIRST (HTML, CSS,
//     JavaScript, then how the three fit together), so the vocabulary lands on a
//     live page before anything is asked of the student. CD02's writing lessons
//     follow (variables, if/else, loops, functions, events, reading an error).
//     Recognition first, then authorship.
//   Module 4 (11) — CD02's Module 6, Claude Code, moved UP to fourth, and opening
//     on CD02/CD03's build-spec lesson (V2401 / V3401) instead of "Meet Claude
//     Code". This is the structural change the whole course is built around: the
//     student leaves the browser BEFORE shipping rather than after, so Module 5 is
//     done with the same tool they will keep using.
//   Module 5 (6) — CD02's Module 5, the shipping module. Ends at a live URL.
//
// WHAT IS DELIBERATELY ABSENT: the REST of CD02/CD03's Module 4 — seven of the
// eight Web Lab build lessons. The app is now built in Claude Code on the student's
// own machine, not in a sandboxed iframe.
//
// Keeping V4401 (the build spec) out of that cut is what makes the module work.
// It is the ONLY lesson in CD04 that hands the student a file: a perfect spec earns
// the project folder — eleven files with their build spec written into CLAUDE.md —
// which is exactly what V4405 then opens in Claude Code four lessons later, and
// what Module 5 publishes. Without it the course would reach "open your own app"
// with nothing to open. It also completes the pair it belongs to: V4101 has them
// choose the idea, this turns that idea into a spec.
//
// `layout` decides which lesson layout renders (UI only):
//   "canvas"    — Lesson | (Concept Canvas + Prompt Lab).
//   "promptLab" — Lesson | Prompt Lab | Editor or Rubric panel.
//   "template"  — Lesson | (brand form + live Starter App Template preview).
//   "webLab"    — Lesson | HTML editor | live page preview + checks.
//   "hotspot"   — Lesson | (question strip + a replica of the real app to click).
//   "checklist" — Lesson | (step checklist + Prompt Lab). Tracks work done OUTSIDE
//                 the app; self-attested, not graded.
//
// `grading` decides how the lesson is assessed (authoritative; decoupled from layout):
//   "canvas"   — interactive self-check, no AI grade.
//   "rubric"   — the student's conversation is graded against a rubric by an AI judge.
//   "template" — self-check: they branded the Starter App Template and ran it.
//   "dom"      — assertions run against the rendered page inside the preview iframe.
//
// `captureProject: true` adds the "My App Idea" card to the Prompt Lab, which
// saves the student's own app to `users_projects`. From then on every lesson chat
// in the course is personalized to it (see promptLabClaudeController).
//
// `lessonID` MUST match the Firestore `lessons` document ID — it keys the shared
// content cache AND the per-user progress doc. CD04's ids are distinct from all
// three earlier courses, so every course tracks progress and caches content
// independently. Reusing an id across courses is not a shortcut: `lesson_content`
// is ONE global collection keyed on the id alone, and a `lessons` doc holds a
// single `courseID`, so seeding a shared id would silently take that lesson away
// from the course it came from.
//
// DEF_VERSION: bump whenever the generated lesson schema or grading changes, so
// per-user cached definitions are treated as stale and regenerated on next load.
//
// It is 5, NOT 1. `CURRENT_DEF_VERSIONS` in backend/src/services/userService.js is
// a single global Set shared by every course (CD01 = 2, CD02 = 3, CD03 = 4), so
// version numbers live in one namespace and a reused number would make another
// course's stale content start passing the freshness gate.
export const DEF_VERSION = 5;

/**
 * The five modules, in display order, with what gates each one.
 *
 * `prereqModule` names the module that must be fully complete before this one opens. It
 * is always the previous module today, and it is still stated explicitly rather than
 * inferred from position — this list briefly held a sixth entry (a Firebase shipping
 * module running in parallel with a GitHub one, both gated on Module 4) and writing the
 * rule down is what made that possible to add and then remove without touching the
 * gating code. The logic lives in v4ModuleGating.js and is tested.
 */
export const V4_MODULES = [
    { moduleNum: 1, titleKey: "table.module1" },
    { moduleNum: 2, titleKey: "table.module2", prereqModule: 1 },
    { moduleNum: 3, titleKey: "table.module3", prereqModule: 2 },
    { moduleNum: 4, titleKey: "table.module4", prereqModule: 3 },
    { moduleNum: 5, titleKey: "table.module5", prereqModule: 4 },
];

const MODULES_1_TO_4 = [
    // ── Module 1 — Your idea, your first win, then foundations ────────────────
    // Opens the same way CD02 and CD03 do: name the thing you want to build, then
    // see it running with your name on it before any theory arrives.
    { lessonID: "V4101", route: "V4_M1_L1", title: "Choose your idea: what do you want to build?", layout: "promptLab", type: "concept", grading: "rubric", captureProject: true },
    { lessonID: "V4102", route: "V4_M1_L2", title: "Your app, running today: make the Starter App Template yours", layout: "template", type: "concept", grading: "template" },
    { lessonID: "V4103", route: "V4_M1_L3", title: "What software is and what a developer actually does", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V4104", route: "V4_M1_L4", title: "How a program runs (input → logic → output)", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V4105", route: "V4_M1_L5", title: "Frontend vs. backend vs. database — the three-part picture", layout: "canvas", type: "concept", grading: "canvas" },
    // From CD01 (CD104). CD02 dropped it and CD03 never had it; it comes back here
    // because "why is there more than one language" is the question every beginner
    // asks the moment the next lesson names three of them.
    { lessonID: "V4106", route: "V4_M1_L6", title: `What a "programming language" is and why there are many`, layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V4107", route: "V4_M1_L7", title: "The three languages of the web: HTML, CSS and JavaScript", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V4108", route: "V4_M1_L8", title: "The developer's toolkit: editor, terminal, browser", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V4109", route: "V4_M1_L9", title: "The single most important skill: breaking a problem into steps", layout: "canvas", type: "concept", grading: "canvas" },

    // ── Module 2 — Working with Claude as a developer ─────────────────────────
    // Identical in all four courses: the conversation is the exercise, graded
    // against a rubric by an AI judge.
    { lessonID: "V4201", route: "V4_M2_L1", title: "What Claude can and can't do for a developer", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V4202", route: "V4_M2_L2", title: "Anatomy of a good coding prompt (context, goal, constraints)", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V4203", route: "V4_M2_L3", title: "Reading Claude's response: code, explanation, and assumptions", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V4204", route: "V4_M2_L4", title: "Iterating: \"that's close, now change this\"", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V4205", route: "V4_M2_L5", title: "Giving Claude an error message and getting unstuck", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V4206", route: "V4_M2_L6", title: "Asking Claude to explain code you don't understand", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V4207", route: "V4_M2_L7", title: "When to trust the AI and when to verify", layout: "promptLab", type: "concept", grading: "rubric" },

    // ── Module 3 — The web page: read it first, then change it ────────────────
    // The hybrid module, and the ordering is the point. CD03's reading pages run
    // first (V4301-V4304, authored scaffolds in starterTemplate/authoredLessons.js):
    // every page already works and asks for ONE edit, so the words tag, rule and
    // click are earned on a live page before any of them is a topic. CD02's
    // writing lessons follow, and land better for it.
    //
    // `multiFile` puts index.html / style.css / script.js on tabs over the one
    // scaffold document, splitting it on open and joining it back before every run
    // (components/PromptLabHub/webLabFiles.js). Nothing about grading changes — the
    // preview still runs one joined page — so the flag is purely how the code is
    // PRESENTED.
    //
    // It is on for EVERY webLab lesson in CD04, which is every lesson in this
    // module bar V4304, and there are no others in the course. That is the point:
    // the first three lessons exist to teach that a page is three different
    // languages, and following them with six lessons that stack all three back
    // inside one file would unteach it two lessons later. A student who meets both
    // shapes with no explanation cannot tell which one is normal.
    //
    // Only V4301/V4302/V4303 (aliased from CD03) and V4308 (CD02's V2307) have
    // authored scaffolds; the rest are generated, so the split has to survive a
    // starter nobody here wrote. See the multi-block handling in webLabFiles.js.
    //
    // The flag is read only by WebLabLayout, so putting it on a non-`webLab` lesson
    // does nothing.
    { lessonID: "V4301", route: "V4_M3_L1", title: "HTML: the file that holds everything you see", layout: "webLab", type: "concept", grading: "dom", multiFile: true },
    { lessonID: "V4302", route: "V4_M3_L2", title: "CSS: the rules that decide how it looks", layout: "webLab", type: "concept", grading: "dom", multiFile: true },
    // "JavaScript:" below is this title's prefix, matching "HTML:" and "CSS:"
    // above. no-script-url reads it as a javascript: URL scheme; the three parallel
    // titles are deliberate, so silence the rule rather than reword one of them.
    // eslint-disable-next-line no-script-url
    { lessonID: "V4303", route: "V4_M3_L3", title: "JavaScript: the part that makes something happen", layout: "webLab", type: "concept", grading: "dom", multiFile: true },
    // No typing at all — the "how it's all connected" lesson, and the hinge between
    // the reading half of this module and the writing half.
    { lessonID: "V4304", route: "V4_M3_L4", title: "How the three fit together — and where a change goes", layout: "canvas", type: "concept", grading: "canvas" },
    // The writing half. Same three tabs as the reading half above — by here the
    // student is writing JavaScript, and script.js is simply where it goes.
    //
    // V4308 gains the most from the split rather than merely tolerating it: its
    // exercise says the first button's code "lives inside the HTML", which is an
    // `onclick` attribute that really does stay in index.html when the inline
    // <script> moves out. The lesson's whole point — move that code somewhere it
    // can be reused — is now a move between two visible files.
    { lessonID: "V4305", route: "V4_M3_L5", title: "Variables and data in JavaScript", layout: "webLab", type: "concept", grading: "dom", multiFile: true },
    { lessonID: "V4306", route: "V4_M3_L6", title: "Making decisions with if / else", layout: "webLab", type: "concept", grading: "dom", multiFile: true },
    { lessonID: "V4307", route: "V4_M3_L7", title: "Lists and loops — showing many items", layout: "webLab", type: "concept", grading: "dom", multiFile: true },
    { lessonID: "V4308", route: "V4_M3_L8", title: "Functions — reusable actions in JavaScript", layout: "webLab", type: "concept", grading: "dom", multiFile: true },
    { lessonID: "V4309", route: "V4_M3_L9", title: "Buttons, forms and events: making the page respond", layout: "webLab", type: "concept", grading: "dom", multiFile: true },
    { lessonID: "V4310", route: "V4_M3_L10", title: "Reading an error in the browser console without panicking", layout: "webLab", type: "concept", grading: "dom", multiFile: true },

    // ── Module 4 — Claude Code: coding on your own computer ───────────────────
    //
    // The first time the course leaves the browser, and in CD04 it happens BEFORE
    // shipping rather than after. Everything up to here runs in a sandboxed iframe;
    // from here the student works in a real tool on real files, and Module 5 ships
    // what that tool produced.
    //
    // Claude Code needs a PAID Claude plan (Pro, Max, Team, Enterprise or Console
    // credits — the free plan excludes it), which the lessons state up front. That
    // is also why Module 5's checklists keep a browser-only path: this course
    // cannot make finishing conditional on a subscription CodeNec doesn't sell.
    //
    // These lessons describe an external product whose install steps, pricing,
    // model list and on-screen controls change without notice, so NOTHING
    // student-facing here is generated. Prose is pinned server-side in
    // backend/src/data/authoredLessonProse.js, the canvas in
    // pages/claudeDevCourse/authoredCanvases.js, the checklists in
    // authoredChecklists.js and the replica screens in
    // starterTemplate/authoredLessons.js — all four aliased to CD02's ids so the
    // two courses cannot drift apart.
    //
    // SEVEN of the eleven are `hotspot` lessons whose screen is a hand-built
    // REPLICA of ONE control — the tab bar, the folder picker, the session screen,
    // the model menu, the effort menu, the mode selector, the diff. One replica per
    // control is the rule: a single screenshot-sized mock of the whole window can
    // be looked at but teaches none of the operations.
    //
    // The organising rule for the rest: is the student DOING this on their own
    // computer, or READING the window? Doing → `checklist` (self-attested, because
    // it happens in Finder or Explorer where nothing we render can follow it);
    // reading → `hotspot`.
    //
    // The module opens on the build spec rather than on "Meet Claude Code", so the
    // student walks into the install with a project in hand. `downloadOnPerfect`
    // gives a full slate their branded app as a PROJECT FOLDER with the finished
    // spec inside its CLAUDE.md — the file V4405 opens and V4411 comes back to.
    // Graded against what Module 1 already settled as well as its own conversation,
    // so it spends its time on the parts of a spec the student has NOT pinned down
    // yet; see components/PromptLabHub/buildSpecLesson.js, which must list this
    // lesson's id in BUILD_SPEC_LESSON_IDS or it silently grades as an ordinary
    // rubric lesson and re-asks for the app idea.
    { lessonID: "V4401", route: "V4_M4_L1", title: "Turning your idea into a build spec with Claude", layout: "promptLab", type: "concept", grading: "rubric", downloadOnPerfect: true },
    { lessonID: "V4402", route: "V4_M4_L2", title: "Download and install the Claude desktop app", layout: "checklist", type: "concept", grading: "canvas" },
    { lessonID: "V4403", route: "V4_M4_L3", title: "The three tabs: Chat, Cowork and Code", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V4404", route: "V4_M4_L4", title: "Add your project: Local and Select folder", layout: "hotspot", type: "concept", grading: "dom" },
    // V4404 shows the new-session screen and grades recognition of it; this is
    // where the student does it on their own machine. In CD02 this lesson had to be
    // squeezed in as V2611 and read out of order; here it simply sits fifth.
    //
    // Its checklist branches on whether they have the project folder from V4401.
    // Both branches are live: `downloadOnPerfect` only fires on a full slate, so a
    // student whose spec came back short arrives here empty-handed and takes the
    // `fresh` path, where Claude Code writes the first file into an empty folder.
    { lessonID: "V4405", route: "V4_M4_L5", title: "Open your own app in Claude Code", layout: "checklist", type: "concept", grading: "canvas" },
    { lessonID: "V4406", route: "V4_M4_L6", title: "Reading the session screen", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V4407", route: "V4_M4_L7", title: "Choosing a model, and changing it mid-session", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V4408", route: "V4_M4_L8", title: "Effort level: how hard Claude works on a task", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V4409", route: "V4_M4_L9", title: "Permission modes: how much it does without asking", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V4410", route: "V4_M4_L10", title: "Reviewing a change before it touches your files", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V4411", route: "V4_M4_L11", title: "CLAUDE.md, and working on your app safely", layout: "promptLab", type: "concept", grading: "rubric" },
];

// ── Module 5 — Ship it: your app, live ────────────────────────────────────────
//
// The course ends at a URL the student can send to a customer. Not "you could
// deploy it" — deployed.
//
// WHY FIREBASE HOSTING, AND WHAT IT COST TO CHOOSE IT. This module was written as an
// alternative to a GitHub Pages one and the two ran side by side — Module 5 and
// "Module 5.5" — until Cristian picked this one on 2026-08-10. The GitHub module is
// gone; what is worth keeping is the trade it lost on, because it is the thing to
// re-examine if this module's completion rate ever disappoints:
//
//   GitHub Pages had a ZERO-INSTALL PATH. Its checklists branched
//   `claudecode` / `browser`, and the browser branch finished the whole course
//   through github.com with no terminal and no paid plan — which mattered because
//   Module 4 is self-attested and Claude Code needs a paid plan CodeNec does not
//   sell, so students reach here without it. Firebase has no equivalent: the console
//   has no upload-a-folder button, `firebase deploy` is the only door, and a command
//   needs Node. The branch here is `claudecode` / `terminal`, and on the `terminal`
//   side the student types four commands.
//
// What Firebase wins, and why it was chosen: a real address (`your-app.web.app`, no
// `/repo-name/` subpath), no public-repo requirement, rollback from the console, and
// the same project the student's data already lives in the moment they fill in
// `config.js`. Free on the Spark plan with NO payment method, which the lessons state
// plainly because a beginner who suspects a card is coming stops before the end.
//
// IDS ARE `V451x`, NOT `V450x`, and that is not a leftover to tidy up. These lessons
// were built and their content and narration generated under these ids; a lessonID
// keys both the shared `lesson_content` cache and the per-user progress doc, so
// renumbering them to close the gap would orphan every generated doc and every
// ElevenLabs clip and re-bill the module. Routes and labels are what students see and
// those say 5. The ids are internal and stay put.
export const MODULE_5 = [
    // THE SPLIT AT V4512/V4513 is deliberate and is the shape of the module.
    // Making the project is clicking in a console; installing the CLI is a terminal
    // that fails in machine-specific ways. Together they were one checklist with two
    // unrelated ways to get stuck, and a student blocked on `npm` could not tell
    // whether the console half had worked. Apart, each has one failure surface.
    //
    // TWO PRODUCT TRAPS THESE LESSONS EXIST TO PREVENT, both of which cost money or
    // an app if hit, and neither of which a generator knows about:
    //   1. App Hosting is NOT Hosting. Adjacent in the console sidebar, near-identical
    //      name, and it requires the paid Blaze plan. V4512 names it.
    //   2. `firebase init hosting` offers to overwrite index.html, and a student who
    //      says yes has just deleted their app. V4514 teaches
    //      `firebase deploy --project <id>` INSTEAD, which needs only the
    //      `firebase.json` the project template already ships and never prompts.
    { lessonID: "V4511", route: "V4_M5_L1", title: "Version control: the undo history for your whole app", layout: "hotspot", type: "concept", grading: "dom" },
    { lessonID: "V4512", route: "V4_M5_L2", title: "Make your Firebase project", layout: "checklist", type: "concept", grading: "canvas" },
    { lessonID: "V4513", route: "V4_M5_L3", title: "Install the deploy tool and sign in", layout: "checklist", type: "concept", grading: "canvas" },
    { lessonID: "V4514", route: "V4_M5_L4", title: "Publish it: your app gets a real address", layout: "checklist", type: "concept", grading: "canvas" },
    // A deploy you can only do once is a demo. This is the loop they keep — and in
    // this module it is also where rollback lives, which Pages had no equivalent of.
    { lessonID: "V4515", route: "V4_M5_L5", title: "Ship a change: the loop from now on", layout: "checklist", type: "concept", grading: "canvas" },
    { lessonID: "V4516", route: "V4_M5_L6", title: "Where to go next and how to keep learning", layout: "promptLab", type: "concept", grading: "rubric" },
];

// 43 lessons, back to the hand-picked count after the GitHub shipping module was retired.
export const CLAUDE_DEV_V4_COURSE_LESSONS = [...MODULES_1_TO_4, ...MODULE_5];

// Every lesson carries the course's DEF_VERSION so consumers can check a cached
// definition against the version of the course it belongs to. PromptLabHubComponent
// is shared by all four courses, which version independently — it must not assume
// any one course's constant.
CLAUDE_DEV_V4_COURSE_LESSONS.forEach((l) => {
    l.defVersion = DEF_VERSION;
});

export const getV4LessonMetaByRoute = (route) =>
    CLAUDE_DEV_V4_COURSE_LESSONS.find((l) => l.route === route);

/**
 * Given a route, return the route of the next lesson in the registry, or null
 * if this is the last one.
 */
export const getNextV4LessonRoute = (currentRoute) => {
    const idx = CLAUDE_DEV_V4_COURSE_LESSONS.findIndex((l) => l.route === currentRoute);
    if (idx === -1 || idx === CLAUDE_DEV_V4_COURSE_LESSONS.length - 1) return null;
    return CLAUDE_DEV_V4_COURSE_LESSONS[idx + 1].route;
};

/**
 * Given a route, return the route of the previous lesson in the registry, or null
 * if this is the first one (the footer's Back button then falls back to the ToC).
 */
export const getPrevV4LessonRoute = (currentRoute) => {
    const idx = CLAUDE_DEV_V4_COURSE_LESSONS.findIndex((l) => l.route === currentRoute);
    if (idx <= 0) return null;
    return CLAUDE_DEV_V4_COURSE_LESSONS[idx - 1].route;
};

/**
 * Position of a lesson within its module, for a "Module M · Lesson X of Y" label.
 * The module number is parsed from the V4_M{n}_L{k} route and the total counts
 * lessons sharing that module. Returns null for unknown/non-V4 routes.
 *
 * The `_L` suffix on the prefix match is load-bearing now that a module reaches
 * ten lessons: without it `V4_M1_L` would also match nothing, but a bare
 * `V4_M1` prefix would happily match `V4_M10_L1` if a tenth module ever existed.
 *
 */
export const getV4LessonProgress = (route) => {
    const m = /^V4_M(\d+)_L/.exec(route || "");
    if (!m) return null;
    const moduleNum = Number(m[1]);
    const moduleLessons = CLAUDE_DEV_V4_COURSE_LESSONS.filter((l) =>
        l.route.startsWith(`V4_M${moduleNum}_L`)
    );
    const lessonInModule = moduleLessons.findIndex((l) => l.route === route) + 1;
    if (lessonInModule === 0) return null;
    return { moduleNum, lessonInModule, totalInModule: moduleLessons.length };
};
